import { Injectable } from '@angular/core';

const STORAGE_PREFIX = 'seekop:tickets:workspace:v1';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DETAIL_TTL_MS = 24 * 60 * 60 * 1000;

export interface TicketsWorkspaceIdentity {
  idDistribuidor: string;
  idUsuario: string;
}

export interface TicketCaptureDraft {
  ticket: Record<string, unknown>;
  queja: Record<string, unknown>;
  ui: {
    mostrarTipoQuejas: boolean;
    mostrarProfeco: boolean;
  };
  context: {
    idProspectoTicket: string;
    isSeekop8: boolean;
    hideMarca: boolean;
    isProspectoExistente: boolean;
    autoCatalogMode: 'default' | 'middleware' | 'seminuevos';
  };
  archivos: string[];
}

export interface TicketDetailWorkspaceState {
  selectedTicketId: string;
  selectedQuejaId: string;
  expandedTicketIds: string[];
  activeTabIndex: number;
  actionType: string | null;
  actionQuejaId: string | null;
}

export interface StoredWorkspaceState<T> {
  savedAt: string;
  value: T;
}

export interface LatestTicketDraft extends StoredWorkspaceState<TicketCaptureDraft> {
  context: string;
}

interface StoredEntry<T> extends StoredWorkspaceState<T> {
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class TicketsWorkspaceStateService {
  saveDraft(
    identity: TicketsWorkspaceIdentity,
    context: string,
    draft: TicketCaptureDraft,
  ): StoredWorkspaceState<TicketCaptureDraft> | null {
    const stored = this.save(this.draftKey(identity, context), draft, DRAFT_TTL_MS);
    if (stored) {
      this.save(this.latestDraftKey(identity), { context }, DRAFT_TTL_MS);
    }
    return stored;
  }

  readDraft(
    identity: TicketsWorkspaceIdentity,
    context: string,
  ): StoredWorkspaceState<TicketCaptureDraft> | null {
    return this.read(this.draftKey(identity, context));
  }

  deleteDraft(identity: TicketsWorkspaceIdentity, context: string): void {
    this.remove(this.draftKey(identity, context));

    const latest = this.read<{ context: string }>(this.latestDraftKey(identity));
    if (latest?.value.context === context) {
      this.remove(this.latestDraftKey(identity));
    }
  }

  readLatestDraft(identity: TicketsWorkspaceIdentity): LatestTicketDraft | null {
    const latest = this.read<{ context: string }>(this.latestDraftKey(identity));
    if (!latest) return null;

    const draft = this.readDraft(identity, latest.value.context);
    if (!draft) {
      this.remove(this.latestDraftKey(identity));
      return null;
    }

    return {
      context: latest.value.context,
      savedAt: draft.savedAt,
      value: draft.value,
    };
  }

  saveDetailState(
    identity: TicketsWorkspaceIdentity,
    idTicket: string,
    state: TicketDetailWorkspaceState,
  ): StoredWorkspaceState<TicketDetailWorkspaceState> | null {
    return this.save(this.detailKey(identity, idTicket), state, DETAIL_TTL_MS);
  }

  readDetailState(
    identity: TicketsWorkspaceIdentity,
    idTicket: string,
  ): StoredWorkspaceState<TicketDetailWorkspaceState> | null {
    return this.read(this.detailKey(identity, idTicket));
  }

  private save<T>(key: string, value: T, ttl: number): StoredWorkspaceState<T> | null {
    if (typeof window === 'undefined') return null;

    const now = new Date();
    const entry: StoredEntry<T> = {
      savedAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + ttl).toISOString(),
      value,
    };

    try {
      window.localStorage.setItem(key, JSON.stringify(entry));
      return { savedAt: entry.savedAt, value: entry.value };
    } catch (error) {
      console.error('No fue posible guardar el estado del módulo de Tickets:', error);
      return null;
    }
  }

  private read<T>(key: string): StoredWorkspaceState<T> | null {
    if (typeof window === 'undefined') return null;

    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;

      const entry = JSON.parse(raw) as StoredEntry<T>;
      if (!entry?.savedAt || !entry?.expiresAt || entry.value === undefined) {
        this.remove(key);
        return null;
      }

      if (new Date(entry.expiresAt).getTime() <= Date.now()) {
        this.remove(key);
        return null;
      }

      return { savedAt: entry.savedAt, value: entry.value };
    } catch (error) {
      console.error('No fue posible recuperar el estado del módulo de Tickets:', error);
      this.remove(key);
      return null;
    }
  }

  private remove(key: string): void {
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.error('No fue posible eliminar el estado del módulo de Tickets:', error);
    }
  }

  private draftKey(identity: TicketsWorkspaceIdentity, context: string): string {
    return [
      STORAGE_PREFIX,
      'draft',
      this.normalize(identity.idDistribuidor),
      this.normalize(identity.idUsuario),
      this.normalize(context),
    ].join(':');
  }

  private detailKey(identity: TicketsWorkspaceIdentity, idTicket: string): string {
    return [
      STORAGE_PREFIX,
      'detail',
      this.normalize(identity.idDistribuidor),
      this.normalize(identity.idUsuario),
      this.normalize(idTicket),
    ].join(':');
  }

  private latestDraftKey(identity: TicketsWorkspaceIdentity): string {
    return [
      STORAGE_PREFIX,
      'latest-draft',
      this.normalize(identity.idDistribuidor),
      this.normalize(identity.idUsuario),
    ].join(':');
  }

  private normalize(value: string): string {
    return encodeURIComponent(value.trim().toLowerCase());
  }
}
